import React, { useRef, useState } from 'react';

import { socialIcons } from './constants';
import { SocialInputProps } from './models';
import { ContactsType } from 'common/types/clinics';
import useOnClickOutside from 'common/hooks/useClickOutside';

import {
  List,
  Label,
  Error,
  Arrow,
  Wrapper,
  Container,
  InputStyled,
  SocialContainer,
} from './styled';

/**
 * A React component that renders a social input field.
 *
 * @param {Object} props - The props for the component.
 * @param {string} props.id - The ID of the input element.
 * @param {string} props.label - The label for the input element.
 * @param {boolean} props.disabled - Whether the input is disabled.
 * @param {string} props.socialType - The type of social media being used (e.g. 'facebook', 'twitter').
 * @param {string} props.errorMessage - An error message to display if there is an issue with the input.
 * @param {Function} props.register - A function to register the input element with a form library.
 * @param {Function} props.onTypeSelect - A function to handle selecting a social media type.
 * @param {...Object} props.rest - Any additional props to pass to the input element.
 */
const SocialInput = ({
  id,
  label,
  disabled,
  socialType,
  errorMessage,
  register,
  onTypeSelect,
  ...rest
}: SocialInputProps) => {
  const ref = useRef(null);

  const [visible, setVisible] = useState(false);

  useOnClickOutside(ref, () => setVisible(false));

  const toggleVisible = () => {
    if (disabled) return;
    setVisible(!visible);
  };

  return (
    <Wrapper>
      {label && (
        <Label errorMessage={errorMessage} htmlFor={id}>
          {label}
        </Label>
      )}

      <Container errorMessage={errorMessage}>
        <SocialContainer ref={ref} disabled={disabled}>
          <div onClick={toggleVisible}>
            {socialIcons[socialType]}
            <Arrow $visible={visible} />
          </div>

          {visible && (
            <List>
              {Object.keys(socialIcons).map((key) => (
                <div
                  key={key}
                  onClick={() => onTypeSelect(key as ContactsType)}
                >
                  {socialIcons[key as ContactsType]}
                </div>
              ))}
            </List>
          )}
        </SocialContainer>

        <InputStyled
          id={id}
          disabled={disabled}
          errorMessage={errorMessage}
          {...register(id)}
          {...rest}
        />
      </Container>

      {errorMessage && <Error>{errorMessage}</Error>}
    </Wrapper>
  );
};

export default SocialInput;
